import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../config/api-client";
import { API_ENDPOINTS } from "../config/api-endpoints";

/**
 * Product Detail API Types
 */

export interface ProductPriceDetail {
  currency: string | null;
  value: number | null;
}

export interface ProductPrices {
  listPrice: ProductPriceDetail;
  salePrice: ProductPriceDetail;
  campaignPrice: ProductPriceDetail;
}

export interface ProductAttribute {
  attributeId: string;
  attributeName: string;
  attributeValueId: string;
  attributeValue: string | null;
  isVariantable: boolean;
}

export interface ProductImage {
  _id: string;
  imageUrl: string;
  productId: string;
  isPrimary: boolean;
  position: number;
}

export interface ProductCategory {
  categoryId: string;
  name: string;
  url: string;
}

export interface ProductVariant {
  productSellerId: string;
  productId: string;
  sellerId: string;
  sellerName: string;
  attributeName: string;
  attributeValue: string;
  hasAnyStock: boolean;
  isOpen: boolean;
  isSelected: boolean;
  productSeoUrl: string;
  sellerSeoUrl: string;
}

export interface OtherSellerInfo {
  id: string;
  name: string;
  seoName: string;
}

export interface OtherSellerMerchant {
  id: string;
  name: string;
}

export interface OtherSellerOffer {
  id: string;
  url: string;
  stockCode: string;
  stockCount: number;
  inStock: boolean;
  prices: ProductPrices;
}

export interface OtherSeller {
  seller: OtherSellerInfo;
  merchant: OtherSellerMerchant;
  offer: OtherSellerOffer;
}

export interface SlicerGroupValue {
  valueId: string;
  valueName: string;
  isSelected: boolean;
  hasAnyStock: boolean;
  productId: string;
  offerId: string;
  productSeoUrl: string;
  imageUrl?: string;
}

export interface SlicerGroupItem {
  attributeId: string;
  attributeName: string;
  values: SlicerGroupValue[];
}

export interface ProductDetailData {
  offerId: string;
  /**
   * Product ObjectId. Favori uçları bu id ile çalışır; ürün detayı zaten id
   * ile çağrıldığı için backend'e sonradan eklendi — eski sürümlerde gelmez,
   * bu yüzden opsiyonel (bkz. `resolveFavoriteProductId`).
   */
  productId?: string;
  name: string;
  displayName: string;
  sellerName: string;
  sellerSeoName: string;
  sellerLogoUrl: string;
  brandId: string;
  brandName: string;
  brandSeoName: string;
  categoryId: string;
  categories: ProductCategory[];
  code: string;
  stockCode: string;
  familyId: string;
  stockCount: number;
  inStock: boolean;
  detailHTML: string;
  additionalDetailHTML: string;
  attributes: ProductAttribute[];
  images: ProductImage[];
  prices: ProductPrices;
  firstVariantGroup: ProductVariant[];
  secondVariantGroup: ProductVariant[];
  slicerGroups: SlicerGroupItem[];
  otherSellers: OtherSeller[];
  /** Misafirde her zaman false — kalp boş çizilir, gizlenmez. */
  isFavorite?: boolean;
}

export interface ProductDetailResponse {
  url: string;
  status: string;
  data: ProductDetailData;
}

/**
 * Hook to fetch product detail by product ID
 * @param productId - Product ID
 * @param offerId - Optional offer ID (for filtering by specific seller offer)
 */
export const useProductDetail = (
  productId: string | null,
  offerId?: string | null,
  preview?: string | null
) => {
  return useQuery({
    queryKey: ["product-detail", productId, offerId ?? null, preview ?? null],
    queryFn: async (): Promise<ProductDetailResponse> => {
      if (!productId) {
        throw new Error("Product ID is required");
      }
      const url = API_ENDPOINTS.getProductDetail(productId);
      const params: Record<string, string> = {};
      if (offerId) params.offerId = offerId;
      if (preview) params.preview = preview;
      const response = await apiClient.get<ProductDetailResponse>(url, {
        params: Object.keys(params).length ? params : undefined,
      });

      return response;
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

